import React from 'react';
import MediaManager from './MediaManager';
import RichTextEditor from './RichTextEditor';

/*
 * Renders the right input control for a PIM attribute based on its field type.
 * `value` is the canonical value shape produced by the backend field-type
 * registry; `onChange(newValue)` reports edits in the same shape.
 */
export default function FieldWidget({ sku, attribute, value, onChange, scope, disabled }) {
  const { type, settings = {}, options = [], option_labels = {} } = attribute;
  const labelFor = (code) => option_labels?.[code] || code;

  switch (type) {
    case 'textarea':
      return (
        <textarea value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      );

    case 'rich_text':
      return <RichTextEditor value={value} onChange={onChange} disabled={disabled} />;

    case 'number':
      return (
        <input
          type="number"
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );

    case 'boolean':
      return (
        <label className="pim-check">
          <input
            type="checkbox"
            checked={!!value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          {value ? 'Yes' : 'No'}
        </label>
      );

    case 'date':
      return (
        <input type="date" value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value || null)} />
      );

    case 'currency': {
      const v = value || {};
      const currencies = settings.currencies || [];
      return (
        <div className="pim-row">
          <input
            type="number"
            placeholder="Amount"
            value={v.amount ?? ''}
            disabled={disabled}
            onChange={(e) => onChange({ ...v, amount: e.target.value === '' ? null : Number(e.target.value) })}
          />
          {currencies.length ? (
            <select value={v.currency || ''} disabled={disabled} onChange={(e) => onChange({ ...v, currency: e.target.value })}>
              <option value="">Currency</option>
              {currencies.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              placeholder="Currency"
              value={v.currency || ''}
              disabled={disabled}
              onChange={(e) => onChange({ ...v, currency: e.target.value.toUpperCase() })}
            />
          )}
        </div>
      );
    }

    case 'metric': {
      const v = value || {};
      const units = settings.units || [];
      return (
        <div className="pim-row">
          <input
            type="number"
            placeholder="Amount"
            value={v.amount ?? ''}
            disabled={disabled}
            onChange={(e) => onChange({ ...v, amount: e.target.value === '' ? null : Number(e.target.value) })}
          />
          {units.length ? (
            <select value={v.unit || ''} disabled={disabled} onChange={(e) => onChange({ ...v, unit: e.target.value })}>
              <option value="">Unit</option>
              {units.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          ) : (
            <input type="text" placeholder="Unit" value={v.unit || ''} disabled={disabled} onChange={(e) => onChange({ ...v, unit: e.target.value })} />
          )}
        </div>
      );
    }

    case 'simpleselect':
      if (options.length && !settings.allow_arbitrary) {
        return (
          <select value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value || null)}>
            <option value="">(none)</option>
            {options.map((o) => (
              <option key={o} value={o}>{labelFor(o)}</option>
            ))}
          </select>
        );
      }
      return <input type="text" value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value || null)} />;

    case 'multiselect': {
      const arr = Array.isArray(value) ? value : [];
      if (options.length && !settings.allow_arbitrary) {
        const toggle = (o) =>
          onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
        return (
          <div className="pim-checks">
            {options.map((o) => (
              <label key={o} className="pim-check">
                <input type="checkbox" checked={arr.includes(o)} disabled={disabled} onChange={() => toggle(o)} />
                {labelFor(o)}
              </label>
            ))}
          </div>
        );
      }
      return (
        <input
          type="text"
          placeholder="comma,separated,values"
          value={arr.join(', ')}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
        />
      );
    }

    case 'image':
    case 'image_carousel':
      return (
        <MediaManager
          sku={sku}
          attribute={attribute}
          value={value}
          scope={scope}
          disabled={disabled}
          onChange={onChange}
        />
      );

    case 'reference':
      return (
        <input
          type="text"
          placeholder="Referenced SKU"
          value={(value && value.product_identifier) || ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value ? { product_identifier: e.target.value } : null)}
        />
      );

    case 'text':
    default:
      return <input type="text" value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
  }
}
